import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import {
  useAddedVCAgentSessionUnitsMutation,
  useCreateVCAgentSessionMutation,
  useLazyGetVCAgentSessionsIdQuery,
  useStartVCAgentSessionMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import {
  areUnitIdsEqual,
  canPersistAgentSessionUnitSelection,
  extractSelectedUnitIdsFromCards,
  filterSelectedCardsBySearch,
  isSearchSubTypeBlockedByExistingSession,
  normalizeUnitIds,
} from 'utils/AICardPanelHelper';
import { extractApiErrorMessage } from 'utils/helpers';

import type ISessionVCItem from 'store/slices/valueChainMap/interfaces/ISessionVCItem';
import type { IUseAICardPanelSessionParams } from '../interfaces/IUseAICardPanelSessionParams';
import type { IUseAICardPanelSessionResult } from '../interfaces/IUseAICardPanelSessionResult';

const useAICardPanelSession = ({
  open,
  onClose,
  onAnalyzeBom,
  onSearchSuppliersSuccess,
  selectedCards,
  onSyncSelectedCardsFromSession,
  onResetAll,
  sessionId,
  setSessionId,
  existingSession,
  isVCAgentSessionsLoaded,
  isAIDraftUnitsReadyForSelectionSync,
  selectedUnitIds,
  onStartMonitoring,
  createAgentConfigId,
  sessionCreateType,
  createAgentSubType,
  skipSessionCreate = false,
  valueChainMapId: valueChainMapIdProp = 0,
}: IUseAICardPanelSessionParams): IUseAICardPanelSessionResult => {
  const { t } = useTranslation();
  const { valueChainMapId: valueChainMapIdParam = '' } = useParams();
  const [search, setSearch] = useState<string>('');
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState<boolean>(false);
  const [isSessionInitialized, setIsSessionInitialized] = useState<boolean>(false);
  const [resolvedSession, setResolvedSession] = useState<ISessionVCItem | undefined>(undefined);
  const [savedSelectedUnitIds, setSavedSelectedUnitIds] = useState<number[]>([]);
  const [debouncedSelectedUnitIds, setDebouncedSelectedUnitIds] = useState<number[]>([]);
  const syncVersionRef = useRef<number>(0);
  const isSessionInitializedRef = useRef<boolean>(false);
  const hasPendingLocalSelectionChangesRef = useRef<boolean>(false);
  const selectedUnitIdsRef = useRef<number[]>(selectedUnitIds);
  const lastOpenedSelectedUnitIdsRef = useRef<number[]>(selectedUnitIds);
  const savedSelectedUnitIdsRef = useRef<number[]>(savedSelectedUnitIds);
  const isCreateSessionRequestedRef = useRef<boolean>(false);
  const isNTierStartFlowInProgressRef = useRef<boolean>(false);
  const selectionDebounceTimeoutRef = useRef<number | undefined>(undefined);
  const wasOpenRef = useRef<boolean>(open);
  const parsedFromProp = Number(valueChainMapIdProp);
  const parsedFromParam = Number(valueChainMapIdParam);
  const parsedValueChainMapId = (
    Number.isInteger(parsedFromProp) && parsedFromProp > 0
      ? parsedFromProp
      : parsedFromParam
  );
  const hasValidValueChainMapId = Number.isInteger(parsedValueChainMapId) && parsedValueChainMapId > 0;
  // create/get session APIs take string map ids; start/units mutations take number.
  const sessionValueChainMapId = hasValidValueChainMapId
    ? String(parsedValueChainMapId)
    : valueChainMapIdParam;

  const [fetchVCAgentSessionById] = useLazyGetVCAgentSessionsIdQuery();

  const [createVCAgentSession, { isLoading: isCreateVCAgentSessionLoading }] = useCreateVCAgentSessionMutation();
  const [
    addedVCAgentSessionUnits,
    { isLoading: isAddedVCAgentSessionUnitsLoading },
  ] = useAddedVCAgentSessionUnitsMutation();
  const [startVCAgentSession, { isLoading: isStartVCAgentSessionLoading }] = useStartVCAgentSessionMutation();

  const sessionStatus = resolvedSession?.status || existingSession?.status;
  const sessionSubType = resolvedSession?.subType || existingSession?.subType;
  const isBillOfMaterialsSession = sessionSubType
    === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE;
  const isSupplierDiscoverySession = sessionSubType
    === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE;
  const isSelectionLocked = sessionStatus === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
    || sessionStatus === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS_LEGACY
    || sessionStatus === VALUE_CHAIN_AI_AGENT.STATUS_PROCESSING
    || sessionStatus === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW;
  const isSelectionHydrating = open
    && Boolean(sessionId || existingSession?.id)
    && !isSessionInitialized;
  const isSelectionSaved = areUnitIdsEqual(selectedUnitIds, savedSelectedUnitIds);
  const isSelectionSyncScheduled = isSessionInitialized
    && !areUnitIdsEqual(selectedUnitIds, debouncedSelectedUnitIds);
  const isSelectionSyncPending = isSelectionSyncScheduled || isAddedVCAgentSessionUnitsLoading;
  const isSearchSuppliersEnabled = selectedUnitIds.length > 0 && (
    isSelectionLocked
    || (
      open
      && hasValidValueChainMapId
      && Boolean(sessionId)
      && isAIDraftUnitsReadyForSelectionSync
      && !isSelectionHydrating
      && isSessionInitialized
      && !isSelectionSyncPending
    )
  );

  useEffect(() => {
    isSessionInitializedRef.current = isSessionInitialized;
  }, [isSessionInitialized]);

  useEffect(() => {
    selectedUnitIdsRef.current = selectedUnitIds;
  }, [selectedUnitIds]);

  useEffect(() => {
    if (open) {
      lastOpenedSelectedUnitIdsRef.current = selectedUnitIds;
    }
  }, [open, selectedUnitIds]);

  useEffect(() => {
    savedSelectedUnitIdsRef.current = savedSelectedUnitIds;
  }, [savedSelectedUnitIds]);

  useEffect(() => {
    if (!open || !isSessionInitialized) {
      return;
    }

    hasPendingLocalSelectionChangesRef.current = !areUnitIdsEqual(
      selectedUnitIds,
      savedSelectedUnitIds,
    );
  }, [isSessionInitialized, open, savedSelectedUnitIds, selectedUnitIds]);

  const applySessionSelectedUnitIds = useCallback((nextSelectedUnitIds: number[]) => {
    const currentSelectedUnitIds = selectedUnitIdsRef.current;
    const doesSessionMatchLocalSelection = areUnitIdsEqual(
      nextSelectedUnitIds,
      currentSelectedUnitIds,
    );

    if (isSessionInitializedRef.current && !doesSessionMatchLocalSelection) {
      return;
    }

    if (!doesSessionMatchLocalSelection && hasPendingLocalSelectionChangesRef.current) {
      return;
    }

    if (!areUnitIdsEqual(savedSelectedUnitIdsRef.current, nextSelectedUnitIds)) {
      setSavedSelectedUnitIds(nextSelectedUnitIds);
    }

    setDebouncedSelectedUnitIds((prevSelectedUnitIds) => (
      areUnitIdsEqual(prevSelectedUnitIds, nextSelectedUnitIds)
        ? prevSelectedUnitIds
        : nextSelectedUnitIds
    ));

    if (!doesSessionMatchLocalSelection) {
      onSyncSelectedCardsFromSession(nextSelectedUnitIds);
    }
  }, [onSyncSelectedCardsFromSession]);

  const syncSelectedUnitIds = useCallback(async (nextSelectedUnitIds: number[]) => {
    if (!sessionId || !hasValidValueChainMapId || !isAIDraftUnitsReadyForSelectionSync) {
      return false;
    }

    const boundSession = resolvedSession || existingSession;
    if (!canPersistAgentSessionUnitSelection(boundSession?.status)) {
      return false;
    }

    if (areUnitIdsEqual(nextSelectedUnitIds, savedSelectedUnitIds)) {
      return true;
    }

    syncVersionRef.current += 1;
    const currentSyncVersion = syncVersionRef.current;

    try {
      await addedVCAgentSessionUnits({
        valueChainMapId: parsedValueChainMapId,
        sessionId,
        itemsId: nextSelectedUnitIds,
      }).unwrap();

      if (syncVersionRef.current !== currentSyncVersion) {
        return false;
      }

      setSavedSelectedUnitIds(nextSelectedUnitIds);
      setDebouncedSelectedUnitIds(nextSelectedUnitIds);
      return true;
    } catch (error) {
      if (syncVersionRef.current !== currentSyncVersion) {
        return false;
      }

      const nestMessage = extractApiErrorMessage(error);
      toast.error(nestMessage
        ? `${t('notifications.valueChainUnitsAddedToSessionError')} — ${nestMessage}`
        : t('notifications.valueChainUnitsAddedToSessionError'));
      return false;
    }
  }, [
    addedVCAgentSessionUnits,
    existingSession,
    hasValidValueChainMapId,
    isAIDraftUnitsReadyForSelectionSync,
    parsedValueChainMapId,
    resolvedSession,
    savedSelectedUnitIds,
    sessionId,
    t,
  ]);

  const ensureSelectionIsSynced = useCallback(async () => {
    if (isSelectionSaved) {
      return true;
    }

    if (selectionDebounceTimeoutRef.current) {
      window.clearTimeout(selectionDebounceTimeoutRef.current);
      selectionDebounceTimeoutRef.current = undefined;
    }

    setDebouncedSelectedUnitIds(selectedUnitIds);
    return syncSelectedUnitIds(selectedUnitIds);
  }, [isSelectionSaved, selectedUnitIds, syncSelectedUnitIds]);

  useEffect(() => {
    const wasOpen = wasOpenRef.current;

    if (wasOpen && !open) {
      if (isNTierStartFlowInProgressRef.current) {
        isNTierStartFlowInProgressRef.current = false;
      }
      const lastOpenedSelectedUnitIds = lastOpenedSelectedUnitIdsRef.current;
      const hasPendingSelectionChanges = !areUnitIdsEqual(
        lastOpenedSelectedUnitIds,
        savedSelectedUnitIdsRef.current,
      );

      if (hasPendingSelectionChanges) {
        if (selectionDebounceTimeoutRef.current) {
          window.clearTimeout(selectionDebounceTimeoutRef.current);
          selectionDebounceTimeoutRef.current = undefined;
        }

        setDebouncedSelectedUnitIds(lastOpenedSelectedUnitIds);
        syncSelectedUnitIds(lastOpenedSelectedUnitIds).catch(() => {});
      }

      setSearch('');
      setIsResetConfirmOpen(false);
      setIsSessionInitialized(false);
      setResolvedSession(undefined);
      setDebouncedSelectedUnitIds([]);
      hasPendingLocalSelectionChangesRef.current = false;
      isCreateSessionRequestedRef.current = false;
    }

    wasOpenRef.current = open;
  }, [open, syncSelectedUnitIds]);

  useEffect(() => {
    if (!open || !isSessionInitialized) {
      return;
    }
    if (selectionDebounceTimeoutRef.current) {
      window.clearTimeout(selectionDebounceTimeoutRef.current);
      selectionDebounceTimeoutRef.current = undefined;
    }
    if (areUnitIdsEqual(selectedUnitIds, debouncedSelectedUnitIds)) {
      return;
    }

    selectionDebounceTimeoutRef.current = window.setTimeout(() => {
      setDebouncedSelectedUnitIds(selectedUnitIds);
      selectionDebounceTimeoutRef.current = undefined;
    }, VALUE_CHAIN_AI_AGENT.CARD_PANEL_SYNC_DELAY);
  }, [debouncedSelectedUnitIds, isSessionInitialized, open, selectedUnitIds]);

  useEffect(() => () => {
    if (selectionDebounceTimeoutRef.current) {
      window.clearTimeout(selectionDebounceTimeoutRef.current);
      selectionDebounceTimeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (
      !open
      || !hasValidValueChainMapId
      || sessionId
      || !isVCAgentSessionsLoaded
      || !existingSession
      || (sessionCreateType && existingSession.type !== sessionCreateType)
    ) {
      return;
    }

    setSessionId(existingSession.id);
    setIsSessionInitialized(false);
    setResolvedSession(undefined);
    setDebouncedSelectedUnitIds([]);
    hasPendingLocalSelectionChangesRef.current = false;
  }, [
    existingSession,
    hasValidValueChainMapId,
    isVCAgentSessionsLoaded,
    open,
    sessionCreateType,
    sessionId,
    setSessionId,
  ]);

  useEffect(() => {
    if (
      !open
      || skipSessionCreate
      || !hasValidValueChainMapId
      || sessionId
      || !!existingSession?.id
      || !isVCAgentSessionsLoaded
      || isCreateVCAgentSessionLoading
      || !createAgentConfigId
      || isCreateSessionRequestedRef.current
    ) {
      return;
    }

    isCreateSessionRequestedRef.current = true;

    createVCAgentSession({
      valueChainMapId: sessionValueChainMapId,
      data: {
        type: sessionCreateType || VALUE_CHAIN_AI_AGENT.SESSION_TYPE,
        ...(createAgentSubType ? { subType: createAgentSubType } : {}),
        agentConfigId: createAgentConfigId,
      },
    })
      .unwrap()
      .then((response) => {
        const createdSessionId = response.data?.id;
        if (!createdSessionId) {
          isCreateSessionRequestedRef.current = false;
          return;
        }

        const nextSelectedUnitIds = normalizeUnitIds(
          (response.data.selectedUnits || []).map(({ unitId }) => unitId),
        );

        setSessionId(createdSessionId);
        applySessionSelectedUnitIds(nextSelectedUnitIds);
        setIsSessionInitialized(true);
      })
      .catch(() => {
        isCreateSessionRequestedRef.current = false;
        setIsSessionInitialized(true);
      });
  }, [
    applySessionSelectedUnitIds,
    createVCAgentSession,
    isCreateVCAgentSessionLoading,
    open,
    sessionId,
    setSessionId,
    sessionValueChainMapId,
    hasValidValueChainMapId,
    isVCAgentSessionsLoaded,
    existingSession,
    createAgentConfigId,
    createAgentSubType,
    sessionCreateType,
    skipSessionCreate,
  ]);

  useEffect(() => {
    let isCancelled = false;

    if (!open || !sessionId || !hasValidValueChainMapId) {
      return undefined;
    }

    if (isNTierStartFlowInProgressRef.current) {
      return undefined;
    }

    setIsSessionInitialized(false);
    setResolvedSession(undefined);
    setDebouncedSelectedUnitIds([]);
    hasPendingLocalSelectionChangesRef.current = false;

    fetchVCAgentSessionById({
      valueChainMapId: sessionValueChainMapId,
      sessionId,
    }, false)
      .unwrap()
      .then((response) => {
        if (isCancelled) {
          return;
        }

        setResolvedSession(response.data);
        applySessionSelectedUnitIds(normalizeUnitIds(
          (response.data.selectedUnits || []).map(({ unitId }) => unitId),
        ));
        setIsSessionInitialized(true);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setResolvedSession(undefined);
        setIsSessionInitialized(true);
      });

    return () => {
      isCancelled = true;
    };
  }, [
    applySessionSelectedUnitIds,
    fetchVCAgentSessionById,
    hasValidValueChainMapId,
    open,
    sessionId,
    sessionValueChainMapId,
  ]);

  useEffect(() => {
    if (!open || !sessionId || !hasValidValueChainMapId || !isSessionInitialized) {
      return;
    }
    if (isNTierStartFlowInProgressRef.current) {
      return;
    }
    if (!isAIDraftUnitsReadyForSelectionSync) {
      return;
    }

    if (areUnitIdsEqual(debouncedSelectedUnitIds, savedSelectedUnitIds)) {
      return;
    }

    const syncSelection = async () => {
      await syncSelectedUnitIds(debouncedSelectedUnitIds);
    };

    syncSelection();
  }, [
    debouncedSelectedUnitIds,
    hasValidValueChainMapId,
    isAIDraftUnitsReadyForSelectionSync,
    isSessionInitialized,
    open,
    savedSelectedUnitIds,
    sessionId,
    syncSelectedUnitIds,
  ]);

  const handleResetAll = useCallback(() => {
    if (!selectedCards.length) {
      return;
    }
    setIsResetConfirmOpen(true);
  }, [selectedCards.length]);

  const handleResetConfirm = useCallback(() => {
    onResetAll();
    setIsResetConfirmOpen(false);
  }, [onResetAll]);

  const handleSearchSuppliers = useCallback((nextSubType: string) => {
    const isBlockedByExistingSession = isSearchSubTypeBlockedByExistingSession(
      nextSubType,
      isBillOfMaterialsSession,
      isSupplierDiscoverySession,
    );

    if (isBlockedByExistingSession || !selectedCards.length) {
      return;
    }

    if (isSelectionLocked) {
      onSearchSuppliersSuccess(selectedCards, existingSession);
      onClose();
    }
  }, [
    existingSession,
    isBillOfMaterialsSession,
    isSelectionLocked,
    isSupplierDiscoverySession,
    onClose,
    onSearchSuppliersSuccess,
    selectedCards,
  ]);

  const handleAnalyzeBom = useCallback(() => {
    if (isSupplierDiscoverySession || !selectedCards.length) {
      return;
    }

    if (isSelectionLocked) {
      onAnalyzeBom(selectedCards, existingSession);
      onClose();
    }
  }, [
    existingSession,
    isSelectionLocked,
    isSupplierDiscoverySession,
    onAnalyzeBom,
    onClose,
    selectedCards,
  ]);

  const confirmNTierCreateWithTemplate = useCallback(async ({
    agentConfigId,
    subType,
  }: {
    agentConfigId: number,
    subType: string,
  }): Promise<boolean> => {
    if (!hasValidValueChainMapId) {
      toast.error(t('notifications.valueChainStartSessionError'));
      return false;
    }

    if (!agentConfigId) {
      toast.error(t('valueChainMap.agentTemplateRequired'));
      return false;
    }

    const isBlockedByExistingSession = isSearchSubTypeBlockedByExistingSession(
      subType,
      isBillOfMaterialsSession,
      isSupplierDiscoverySession,
    );

    if (isBlockedByExistingSession) {
      return false;
    }

    if (sessionId) {
      return true;
    }

    try {
      const createResponse = await createVCAgentSession({
        valueChainMapId: sessionValueChainMapId,
        data: {
          type: VALUE_CHAIN_AI_AGENT.SESSION_TYPE,
          subType,
          agentConfigId,
        },
      }).unwrap();

      const createdSessionId = createResponse.data?.id;
      if (!createdSessionId) {
        throw new Error('Failed to create AI session');
      }

      const nextSelectedUnitIds = normalizeUnitIds(
        (createResponse.data.selectedUnits || []).map(({ unitId }) => unitId),
      );

      isCreateSessionRequestedRef.current = true;
      setSessionId(createdSessionId);
      applySessionSelectedUnitIds(nextSelectedUnitIds);
      setIsSessionInitialized(true);
      return true;
    } catch {
      toast.error(t('notifications.valueChainStartSessionError'));
      return false;
    }
  }, [
    applySessionSelectedUnitIds,
    createVCAgentSession,
    hasValidValueChainMapId,
    isBillOfMaterialsSession,
    isSupplierDiscoverySession,
    sessionId,
    sessionValueChainMapId,
    setSessionId,
    t,
  ]);

  const confirmNTierStartWithTemplate = useCallback(async ({
    agentConfigId,
    subType,
  }: {
    agentConfigId: number,
    subType: string,
  }) => {
    if (!hasValidValueChainMapId) {
      toast.error(t('notifications.valueChainStartSessionError'));
      return;
    }

    if (!agentConfigId) {
      toast.error(t('valueChainMap.agentTemplateRequired'));
      return;
    }

    const unitIdsForStart = selectedUnitIds.length
      ? selectedUnitIds
      : extractSelectedUnitIdsFromCards(selectedCards);

    if (!unitIdsForStart.length) {
      toast.error(t('valueChainMap.aiPanelSelectionRequired'));
      return;
    }

    const isBom = subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE;
    const isBlockedByExistingSession = isSearchSubTypeBlockedByExistingSession(
      subType,
      isBillOfMaterialsSession,
      isSupplierDiscoverySession,
    );

    if (isBlockedByExistingSession) {
      return;
    }

    const activeSessionId = sessionId;

    if (!activeSessionId) {
      toast.error(t('notifications.valueChainStartSessionError'));
      return;
    }

    isNTierStartFlowInProgressRef.current = true;

    try {
      await addedVCAgentSessionUnits({
        valueChainMapId: parsedValueChainMapId,
        sessionId: activeSessionId,
        itemsId: unitIdsForStart,
      }).unwrap();
      savedSelectedUnitIdsRef.current = unitIdsForStart;
      setSavedSelectedUnitIds(unitIdsForStart);
      setDebouncedSelectedUnitIds(unitIdsForStart);
    } catch {
      isNTierStartFlowInProgressRef.current = false;
      toast.error(t('notifications.valueChainUnitsAddedToSessionError'));
      return;
    }

    try {
      const startResponse = await startVCAgentSession({
        valueChainMapId: parsedValueChainMapId,
        sessionId: activeSessionId,
        data: {
          subType,
          agentConfigId,
        },
      }).unwrap();

      if (isBom) {
        onAnalyzeBom(selectedCards, startResponse.data);
      } else {
        onSearchSuppliersSuccess(selectedCards, startResponse.data);
      }
      isNTierStartFlowInProgressRef.current = false;
      onClose();
    } catch {
      isNTierStartFlowInProgressRef.current = false;
      toast.error(t('notifications.valueChainStartSessionError'));
    }
  }, [
    addedVCAgentSessionUnits,
    hasValidValueChainMapId,
    isBillOfMaterialsSession,
    isSupplierDiscoverySession,
    onAnalyzeBom,
    onClose,
    onSearchSuppliersSuccess,
    parsedValueChainMapId,
    selectedCards,
    selectedUnitIds,
    sessionId,
    startVCAgentSession,
    t,
  ]);

  const handleStartMonitoring = useCallback(async () => {
    if (
      !sessionId
      || !hasValidValueChainMapId
      || !selectedCards.length
      || isSelectionHydrating
      || isSelectionSyncPending
    ) {
      return;
    }

    const isSelectionSynced = await ensureSelectionIsSynced();
    if (!isSelectionSynced) {
      return;
    }

    onStartMonitoring?.();
  }, [
    ensureSelectionIsSynced,
    hasValidValueChainMapId,
    isSelectionHydrating,
    isSelectionSyncPending,
    onStartMonitoring,
    selectedCards.length,
    sessionId,
  ]);

  const filteredSelectedCards = useMemo(
    () => filterSelectedCardsBySearch(selectedCards, search),
    [search, selectedCards],
  );

  return {
    search,
    setSearch,
    isResetConfirmOpen,
    setIsResetConfirmOpen,
    isSelectionHydrating,
    isSelectionSyncPending,
    isSearchSuppliersEnabled,
    isSelectionLocked,
    isBillOfMaterialsSession,
    isSupplierDiscoverySession,
    isStartVCAgentSessionLoading,
    isCreateVCAgentSessionLoading,
    filteredSelectedCards,
    handleResetAll,
    handleResetConfirm,
    handleSearchSuppliers,
    handleAnalyzeBom,
    handleStartMonitoring,
    confirmNTierCreateWithTemplate,
    confirmNTierStartWithTemplate,
    resolvedSession,
  };
};

export default useAICardPanelSession;
